<script lang="ts">
export default {
  name: 'UIButton',
  inheritAttrs: false,
};

const buttonVariants = cva(
  'btn inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-secondary text-white hover:bg-secondary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input hover:bg-secondary/80',
        secondary: 'bg-secondary hover:bg-secondary/80 text-white',
        ghost: 'hover:bg-secondary hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: '',
        sm: 'btn-sm',
        lg: 'btn-lg',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

interface Props extends /* @vue-ignore */ ButtonVariants {
  as?: string;
  class?: string;
  type?: 'button' | 'submit' | 'reset';
  variant?: ButtonVariants['variant'];
  size?: ButtonVariants['size'];
}

const props = withDefaults(defineProps<Props>(), {
  as: 'button',
  type: 'button',
});

const buttonClass = computed(() => cn(buttonVariants({ variant: props.variant, size: props.size }), props.class));
</script>

<template>
  <component :is="as" :class="buttonClass" :type="type" v-bind="$attrs">
    <slot />
  </component>
</template>
